import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../utils/axiosConfig';
import OrganizationChart from './OrganizationChart';
import OrgMapView from './OrgMapView';
import { useJerarquia } from './orgShared';
import { normalizarTexto } from '../utils/texto';
import { FaSitemap, FaSearch, FaTimes, FaChevronDown, FaFilter, FaMap } from 'react-icons/fa';

const VIEW_TABS = [
  { key: 'mapa', label: 'Mapa', Icon: FaMap, title: 'Mapa que se despliega hacia abajo' },
  { key: 'diagrama', label: 'Diagrama', Icon: FaSitemap, title: 'Vista jerárquica (diagrama)' },
];
const VIEW_KEYS = VIEW_TABS.map((t) => t.key);

const OrganizationView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [gerencias, setGerencias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [areas, setAreas] = useState([]);

  const [selectedGerencia, setSelectedGerencia] = useState(null);
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [orgchartSearch, setOrgchartSearch] = useState('');
  const [showOrgchartSearchResults, setShowOrgchartSearchResults] = useState(false);
  const [selectedOrgchartEmployee, setSelectedOrgchartEmployee] = useState(null);
  // Solo quedan Mapa y Diagrama: una vista vieja guardada (lista, explorar...) cae en Mapa
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('orgViewMode');
    return VIEW_KEYS.includes(saved) ? saved : 'mapa';
  });

  // Leer parámetros de URL
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, []);

  // Aplicar parámetros de URL cuando los datos de filtros estén cargados
  useEffect(() => {
    if (gerencias.length > 0) {
      const urlGerencia = searchParams.get('gerencia');
      const urlDepartamento = searchParams.get('departamento');
      const urlArea = searchParams.get('area');

      if (urlGerencia && !selectedGerencia) {
        setSelectedGerencia(urlGerencia);
      }
      if (urlDepartamento && !selectedDepartamento) {
        setSelectedDepartamento(urlDepartamento);
      }
      if (urlArea && !selectedArea) {
        setSelectedArea(urlArea);
      }
    }
  }, [gerencias, searchParams]);

  const fetchFilterOptions = async () => {
    try {
      const [deptosRes, gerenciasRes, areasRes] = await Promise.all([
        axiosInstance.get('/api/departamentos'),
        axiosInstance.get('/api/gerencias'),
        axiosInstance.get('/api/areas')
      ]);
      setDepartamentos(deptosRes.data);
      setGerencias(gerenciasRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/empleados");
      setData(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error al cargar los datos para la estructura organizativa.");
    } finally {
      setLoading(false);
    }
  };

  const orgchartQuery = normalizarTexto(orgchartSearch).trim();
  const orgchartSearchResults = data.filter(emp =>
    normalizarTexto(emp.nombre).includes(orgchartQuery) ||
    normalizarTexto(emp.cargo).includes(orgchartQuery) ||
    emp.ficha?.toString().includes(orgchartQuery)
  ).slice(0, 5);

  const hasActiveFilters = selectedGerencia || selectedDepartamento || selectedArea;

  // Persistir el modo de vista elegido
  useEffect(() => {
    localStorage.setItem('orgViewMode', viewMode);
  }, [viewMode]);

  // Datos jerárquicos para el Mapa.
  // El fetch solo se dispara fuera del diagrama clásico.
  const {
    roots: jerarquiaRoots,
    loading: jerarquiaLoading,
    error: jerarquiaError,
  } = useJerarquia({
    gerenciaId: selectedGerencia === 'all' ? null : selectedGerencia,
    departamentoId: selectedDepartamento,
    areaId: selectedArea,
    enabled: viewMode !== 'diagrama' && !!selectedGerencia,
  });

  const clearAllFilters = () => {
    setSelectedGerencia(null);
    setSelectedDepartamento(null);
    setSelectedArea(null);
    setOrgchartSearch('');
    setSelectedOrgchartEmployee(null);
  };

  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 -m-4 md:-m-8">
        <motion.div
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div
            className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-sura-blue animate-spin"
          />
          <p className="text-gray-500 font-medium">Cargando estructura organizativa...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 overflow-hidden">
      {/* Navbar de filtros - Compacta y elegante */}
      <motion.div
        className="shrink-0 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm z-20"
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="px-4 py-3">
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            {/* Título */}
            <div
              className="flex items-center"
            >
              <h1 className="text-lg font-bold text-gray-800 leading-tight whitespace-nowrap">Estructura Organizativa</h1>
            </div>

            {/* Separador */}
            <div className="h-8 w-px bg-gray-200 hidden lg:block" />

            {/* Filtros en línea */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="items-center text-gray-400 hidden 2xl:flex">
                <FaFilter size={11} />
              </div>

              {/* Gerencia */}
              <motion.div className="relative" whileHover={{ scale: 1.02 }}>
                <select
                  value={selectedGerencia || ''}
                  onChange={(e) => {
                    setSelectedGerencia(e.target.value || null);
                    setSelectedDepartamento(null);
                    setSelectedArea(null);
                  }}
                  className={`
                    appearance-none pl-3 pr-8 py-2 text-sm font-medium rounded-lg border-2 transition-all cursor-pointer min-w-[124px] max-w-[170px]
                    ${selectedGerencia
                      ? 'bg-sura-blue/5 border-sura-blue/30 text-sura-blue'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  <option value="">Seleccionar Gerencia</option>
                  {[...gerencias].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(g => (
                    <option key={g.id} value={g.id}>{g.nombre}</option>
                  ))}
                  <option value="all">Ver todo el organigrama</option>
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={10} />
              </motion.div>

              {/* Departamento */}
              <motion.div className="relative" whileHover={{ scale: 1.02 }}>
                <select
                  value={selectedDepartamento || ''}
                  onChange={(e) => {
                    setSelectedDepartamento(e.target.value || null);
                    setSelectedArea(null);
                  }}
                  disabled={!selectedGerencia}
                  className={`
                    appearance-none pl-3 pr-8 py-2 text-sm font-medium rounded-lg border-2 transition-all cursor-pointer min-w-[124px] max-w-[170px]
                    ${!selectedGerencia ? 'opacity-50 cursor-not-allowed' : ''}
                    ${selectedDepartamento
                      ? 'bg-sura-blue/5 border-sura-blue/30 text-sura-blue'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  <option value="">Departamento</option>
                  {departamentos
                    .filter(d => !selectedGerencia || d.gerencia_id === parseInt(selectedGerencia))
                    .map(d => (
                      <option key={d.id} value={d.id}>{d.nombre}</option>
                    ))}
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={10} />
              </motion.div>

              {/* Área */}
              <motion.div className="relative" whileHover={{ scale: 1.02 }}>
                <select
                  value={selectedArea || ''}
                  onChange={(e) => setSelectedArea(e.target.value || null)}
                  disabled={!selectedDepartamento}
                  className={`
                    appearance-none pl-3 pr-8 py-2 text-sm font-medium rounded-lg border-2 transition-all cursor-pointer min-w-[124px] max-w-[170px]
                    ${!selectedDepartamento ? 'opacity-50 cursor-not-allowed' : ''}
                    ${selectedArea
                      ? 'bg-sura-blue/5 border-sura-blue/30 text-sura-blue'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  <option value="">Área</option>
                  {areas
                    .filter(a => !selectedDepartamento || a.departamento_id === parseInt(selectedDepartamento))
                    .map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                </select>
                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={10} />
              </motion.div>

              {/* Botón limpiar filtros */}
              <AnimatePresence>
                {hasActiveFilters && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={clearAllFilters}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaTimes size={10} />
                    Limpiar
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Toggle de vista */}
            <div className="flex items-center bg-gray-100 rounded-xl p-0.5 gap-0.5 shrink-0" role="tablist" aria-label="Modo de vista">
              {VIEW_TABS.map(({ key, label, Icon, title }) => {
                const active = viewMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    title={title}
                    onClick={() => setViewMode(key)}
                    className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? 'text-sura-blue' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {active && (
                      <motion.span
                        layoutId="orgViewPill"
                        className="absolute inset-0 bg-white rounded-lg shadow-sm"
                        transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                      />
                    )}
                    <Icon size={12} className="relative z-10" />
                    <span className="relative z-10 hidden md:inline">{label}</span>
                  </button>
                );
              })}
            </div>

            {/* Buscador */}
            <div className="relative">
              <motion.div
                className={`
                  flex items-center bg-gray-50 rounded-xl border-2 transition-all overflow-hidden
                  ${orgchartSearch ? 'border-sura-blue/30 bg-sura-blue/5' : 'border-gray-200 hover:border-gray-300'}
                `}
                whileHover={{ scale: 1.01 }}
              >
                <FaSearch className="ml-3 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder="Buscar empleado..."
                  value={orgchartSearch}
                  onChange={(e) => {
                    setOrgchartSearch(e.target.value);
                    setShowOrgchartSearchResults(true);
                  }}
                  onFocus={() => setShowOrgchartSearchResults(true)}
                  className="w-36 xl:w-44 px-3 py-2 bg-transparent text-sm outline-none placeholder-gray-400"
                />
                {orgchartSearch && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => {
                      setOrgchartSearch('');
                      setSelectedOrgchartEmployee(null);
                    }}
                    className="mr-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200/50"
                  >
                    <FaTimes size={10} />
                  </motion.button>
                )}
              </motion.div>

              {/* Dropdown de resultados */}
              <AnimatePresence>
                {showOrgchartSearchResults && orgchartSearch && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute z-50 right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden"
                  >
                    {orgchartSearchResults.length > 0 ? (
                      <ul className="py-1 max-h-64 overflow-y-auto">
                        {orgchartSearchResults.map((emp, index) => (
                          <motion.li
                            key={emp.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="px-4 py-3 hover:bg-sura-blue/5 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                            onClick={() => {
                              setSelectedOrgchartEmployee(emp.id);
                              setOrgchartSearch(emp.nombre);
                              setShowOrgchartSearchResults(false);
                            }}
                          >
                            <p className="text-sm font-semibold text-gray-800">{emp.nombre}</p>
                            <p className="text-xs text-gray-500">{emp.cargo}</p>
                          </motion.li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-4 py-4 text-sm text-gray-400 text-center">
                        No se encontraron resultados
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>
        </div>

      </motion.div>

      {/* Canvas del organigrama - Ocupa todo el espacio restante */}
      <div className="flex-1 relative overflow-hidden">
        {!selectedGerencia ? (
          <motion.div
            className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center max-w-md px-6">
              <div
                className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-sura-blue/10 to-blue-100 rounded-2xl flex items-center justify-center animate-gentlePulse"
              >
                <FaSitemap className="text-sura-blue text-3xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-3">Selecciona una gerencia</h2>
              <p className="text-gray-500 mb-6">
                Para visualizar la estructura organizativa, primero selecciona una gerencia en el filtro superior.
                También puedes elegir <span className="font-semibold text-sura-blue">"Ver todo el organigrama"</span> para ver la estructura completa.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <FaFilter size={12} />
                <span>Usa los filtros de arriba para comenzar</span>
              </div>
            </div>
          </motion.div>
        ) : viewMode === 'mapa' ? (
          <OrgMapView
            roots={jerarquiaRoots}
            loading={jerarquiaLoading}
            error={jerarquiaError}
            selectedEmployeeId={selectedOrgchartEmployee}
          />
        ) : (
          <OrganizationChart
            departamentoId={selectedDepartamento}
            gerenciaId={selectedGerencia === 'all' ? null : selectedGerencia}
            areaId={selectedArea}
            employees={data}
            centeredEmployeeId={selectedOrgchartEmployee}
            searchTerm={orgchartSearch}
          />
        )}
      </div>

      {/* Click outside handler para cerrar búsqueda */}
      {showOrgchartSearchResults && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowOrgchartSearchResults(false)}
        />
      )}
    </div>
  );
};

export default OrganizationView;
